import { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Copy, QrCode, Share, CurrencyDollar, Pencil, Download, X } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { QRCodeCanvas } from 'qrcode.react';
import api from '../lib/api';

/**
 * PartnerTab — self-contained panel for a referral partner (Freddy, AJ, future).
 * Renders: signup link card + QR + commission banner (admin only) + Pay Out + customer list.
 * Reads/writes against `/api/customers/{tag}/*` endpoints.
 *
 * Props:
 *   tag         partner slug used in URL + source_tag (e.g. "freddy")
 *   title       display label e.g. "Freddy Clients"
 *   accent      tailwind color prefix: "amber", "indigo", "emerald" ...
 *   companyId   admin's company_id (for building public links)
 *   publicSite  base public URL (defaults to trackmypackage.app)
 *   isViewer    true if the logged-in user is this partner's view-only role
 *   search      optional search text to filter the customer list
 *   tx          i18n helper (en, es, fr)
 */
export default function PartnerTab({ tag, title, accent = 'amber', companyId, publicSite, isViewer, search = '', tx }) {
  const [customers, setCustomers] = useState([]);
  const [commissions, setCommissions] = useState({ rate_usd_per_kg: 1, total_earned: 0, total_paid: 0, total_owed: 0, clients: [] });

  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [payoutAmount, setPayoutAmount] = useState('');
  const [payoutNote, setPayoutNote] = useState('');
  const [payingOut, setPayingOut] = useState(false);

  const [showQR, setShowQR] = useState(false);

  const [editingRate, setEditingRate] = useState(false);
  const [rateDraft, setRateDraft] = useState('');
  const [savingRate, setSavingRate] = useState(false);

  const signupUrl = companyId ? `${publicSite}/join/${companyId}/${tag}` : '';

  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, [search, tag]);

  const fetchAll = async () => {
    try {
      const r = await api.get('/customers', { params: { source_tag: tag, ...(search ? { search } : {}) } });
      setCustomers(r.data || []);
    } catch { setCustomers([]); }
    if (!isViewer) {
      try {
        const c = await api.get(`/customers/${tag}/commissions`);
        setCommissions(c.data || { rate_usd_per_kg: 1, total_earned: 0, total_paid: 0, total_owed: 0, clients: [] });
      } catch { setCommissions({ rate_usd_per_kg: 1, total_earned: 0, total_paid: 0, total_owed: 0, clients: [] }); }
    }
  };

  const openPayout = () => {
    setPayoutAmount(((commissions.total_owed ?? 0) > 0 ? commissions.total_owed : 0).toFixed(2));
    setPayoutNote('');
    setShowPayoutModal(true);
  };

  const recordPayout = async () => {
    const amt = parseFloat(payoutAmount);
    if (!amt || amt <= 0) { toast.error(tx('Enter a positive amount', 'Monto positivo requerido', 'Montant invalide')); return; }
    setPayingOut(true);
    try {
      await api.post(`/customers/${tag}/payouts`, { amount: amt, note: payoutNote });
      toast.success(tx(`Payout of $${amt.toFixed(2)} recorded`, `Pago de $${amt.toFixed(2)} registrado`, `Paiement $${amt.toFixed(2)}`));
      setShowPayoutModal(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || tx('Failed to record payout', 'Error', 'Échec'));
    } finally { setPayingOut(false); }
  };

  const beginEditRate = () => { setRateDraft(String(commissions.rate_usd_per_kg ?? 1)); setEditingRate(true); };
  const saveRate = async () => {
    const r = parseFloat(rateDraft);
    if (!(r >= 0)) { toast.error(tx('Invalid rate', 'Tarifa inválida', 'Taux invalide')); return; }
    setSavingRate(true);
    try {
      await api.post(`/customers/${tag}/rate`, { rate_usd_per_kg: r });
      toast.success(tx(`Rate set to $${r.toFixed(2)}/kg`, `$${r.toFixed(2)}/kg`, `$${r.toFixed(2)}/kg`));
      setEditingRate(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || tx('Failed to save rate', 'Error', 'Échec'));
    } finally { setSavingRate(false); }
  };

  const downloadQR = () => {
    const canvas = document.querySelector(`#${tag}-qr-canvas canvas`);
    if (!canvas) return;
    const url = canvas.toDataURL('image/png');
    const a = document.createElement('a');
    a.href = url;
    a.download = `${tag}-signup-qr.png`;
    document.body.appendChild(a); a.click(); a.remove();
  };

  // Tailwind cannot JIT dynamic class names, so build the shared palette explicitly.
  const PALETTE = {
    amber: {
      cardBg: 'from-amber-50 to-orange-50 border-amber-200/60',
      cardKicker: 'text-amber-700',
      cardBody: 'text-amber-900/80',
      cardCode: 'text-amber-900',
      btnPrimary: 'bg-amber-600 hover:bg-amber-700 text-white',
      btnOutline: 'border-amber-300 text-amber-700 hover:bg-amber-100',
      badgeBg: 'bg-amber-100 text-amber-700 border border-amber-200',
      avatar: 'bg-amber-100 border border-amber-200',
      avatarText: 'text-amber-700',
    },
    indigo: {
      cardBg: 'from-indigo-50 to-sky-50 border-indigo-200/60',
      cardKicker: 'text-indigo-700',
      cardBody: 'text-indigo-900/80',
      cardCode: 'text-indigo-900',
      btnPrimary: 'bg-indigo-600 hover:bg-indigo-700 text-white',
      btnOutline: 'border-indigo-300 text-indigo-700 hover:bg-indigo-100',
      badgeBg: 'bg-indigo-100 text-indigo-700 border border-indigo-200',
      avatar: 'bg-indigo-100 border border-indigo-200',
      avatarText: 'text-indigo-700',
    },
    emerald: {
      cardBg: 'from-emerald-50 to-teal-50 border-emerald-200/60',
      cardKicker: 'text-emerald-700',
      cardBody: 'text-emerald-900/80',
      cardCode: 'text-emerald-900',
      btnPrimary: 'bg-emerald-600 hover:bg-emerald-700 text-white',
      btnOutline: 'border-emerald-300 text-emerald-700 hover:bg-emerald-100',
      badgeBg: 'bg-emerald-100 text-emerald-700 border border-emerald-200',
      avatar: 'bg-emerald-100 border border-emerald-200',
      avatarText: 'text-emerald-700',
    },
    rose: {
      cardBg: 'from-rose-50 to-pink-50 border-rose-200/60',
      cardKicker: 'text-rose-700',
      cardBody: 'text-rose-900/80',
      cardCode: 'text-rose-900',
      btnPrimary: 'bg-rose-600 hover:bg-rose-700 text-white',
      btnOutline: 'border-rose-300 text-rose-700 hover:bg-rose-100',
      badgeBg: 'bg-rose-100 text-rose-700 border border-rose-200',
      avatar: 'bg-rose-100 border border-rose-200',
      avatarText: 'text-rose-700',
    },
    sky: {
      cardBg: 'from-sky-50 to-cyan-50 border-sky-200/60',
      cardKicker: 'text-sky-700',
      cardBody: 'text-sky-900/80',
      cardCode: 'text-sky-900',
      btnPrimary: 'bg-sky-600 hover:bg-sky-700 text-white',
      btnOutline: 'border-sky-300 text-sky-700 hover:bg-sky-100',
      badgeBg: 'bg-sky-100 text-sky-700 border border-sky-200',
      avatar: 'bg-sky-100 border border-sky-200',
      avatarText: 'text-sky-700',
    },
    violet: {
      cardBg: 'from-violet-50 to-fuchsia-50 border-violet-200/60',
      cardKicker: 'text-violet-700',
      cardBody: 'text-violet-900/80',
      cardCode: 'text-violet-900',
      btnPrimary: 'bg-violet-600 hover:bg-violet-700 text-white',
      btnOutline: 'border-violet-300 text-violet-700 hover:bg-violet-100',
      badgeBg: 'bg-violet-100 text-violet-700 border border-violet-200',
      avatar: 'bg-violet-100 border border-violet-200',
      avatarText: 'text-violet-700',
    },
    teal: {
      cardBg: 'from-teal-50 to-cyan-50 border-teal-200/60',
      cardKicker: 'text-teal-700',
      cardBody: 'text-teal-900/80',
      cardCode: 'text-teal-900',
      btnPrimary: 'bg-teal-600 hover:bg-teal-700 text-white',
      btnOutline: 'border-teal-300 text-teal-700 hover:bg-teal-100',
      badgeBg: 'bg-teal-100 text-teal-700 border border-teal-200',
      avatar: 'bg-teal-100 border border-teal-200',
      avatarText: 'text-teal-700',
    },
    orange: {
      cardBg: 'from-orange-50 to-amber-50 border-orange-200/60',
      cardKicker: 'text-orange-700',
      cardBody: 'text-orange-900/80',
      cardCode: 'text-orange-900',
      btnPrimary: 'bg-orange-600 hover:bg-orange-700 text-white',
      btnOutline: 'border-orange-300 text-orange-700 hover:bg-orange-100',
      badgeBg: 'bg-orange-100 text-orange-700 border border-orange-200',
      avatar: 'bg-orange-100 border border-orange-200',
      avatarText: 'text-orange-700',
    },
  };
  const palette = PALETTE[accent] || PALETTE.amber;

  return (
    <div className="space-y-3" data-testid={`${tag}-clients-panel`}>
      {!isViewer && companyId && (
        <div className={`bg-gradient-to-r ${palette.cardBg} border rounded-xl p-4 flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between`} data-testid={`${tag}-link-card`}>
          <div className="flex-1 min-w-0">
            <p className={`text-xs font-semibold ${palette.cardKicker} uppercase tracking-wide mb-1`}>
              {title} {tx('Signup Link', 'Enlace', 'Lien')}
            </p>
            <p className={`text-sm ${palette.cardBody} mb-1`}>
              {tx(`Share — anyone who signs up here is tagged as a ${title.replace(' Clients','')} client.`, `Comparte — cada registro queda marcado.`, `Partagez — chaque inscription est marquée.`)}
            </p>
            <code className={`block mt-1 text-xs sm:text-sm font-mono ${palette.cardCode} break-all`}>{signupUrl}</code>
          </div>
          <div className="flex gap-2 shrink-0">
            <Button
              onClick={() => { navigator.clipboard.writeText(signupUrl); toast.success(tx('Link copied!', '¡Copiado!', 'Copié !')); }}
              className={`${palette.btnPrimary} rounded-lg`}
              data-testid={`copy-${tag}-link-btn`}
            >
              <Copy size={16} className="mr-1.5" /> {tx('Copy', 'Copiar', 'Copier')}
            </Button>
            <Button onClick={() => setShowQR(true)} variant="outline" className={`${palette.btnOutline} rounded-lg`} data-testid={`${tag}-qr-btn`}>
              <QrCode size={16} className="mr-1.5" /> QR
            </Button>
            <Button
              onClick={async () => {
                if (navigator.share) { try { await navigator.share({ title: `${title} Signup`, url: signupUrl }); } catch {} }
                else { navigator.clipboard.writeText(signupUrl); toast.success(tx('Link copied!', '¡Copiado!', 'Copié !')); }
              }}
              variant="outline"
              className={`${palette.btnOutline} rounded-lg`}
              data-testid={`share-${tag}-link-btn`}
            >
              <Share size={16} className="mr-1.5" /> {tx('Share', 'Compartir', 'Partager')}
            </Button>
          </div>
        </div>
      )}

      {customers.length === 0 ? (
        <div className="kpi-card text-center py-10">
          <p className="text-gray-500">
            {tx(`No ${title.replace(' Clients','')} clients yet.`, 'Aún no hay clientes.', 'Aucun client.')}
          </p>
          <p className="text-xs text-gray-400 mt-1">
            {tx('Share the signup link so clients land here automatically.', 'Comparte el enlace de registro.', 'Partagez le lien.')}
          </p>
        </div>
      ) : (
        <>
          {!isViewer && (
            <div className="kpi-card bg-gradient-to-r from-emerald-50 to-cyan-50 border-emerald-200/60 flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between" data-testid={`${tag}-commission-banner`}>
              <div>
                <p className="text-xs font-semibold text-emerald-700 uppercase tracking-wide">
                  {tx(`Owed to ${title.replace(' Clients','')}`, 'Pendiente de pagar', 'Dû')}
                </p>
                <p className="text-3xl font-black text-emerald-900 leading-tight mt-0.5" data-testid={`${tag}-total-owed`}>
                  ${(commissions.total_owed || 0).toFixed(2)}
                </p>
                <p className="text-[11px] text-emerald-700/80 mt-1" data-testid={`${tag}-rate-line`}>
                  {tx('Earned', 'Ganado', 'Gagné')}: ${(commissions.total_earned || 0).toFixed(2)} · {tx('Paid', 'Pagado', 'Payé')}: ${(commissions.total_paid || 0).toFixed(2)}
                  {' · '}
                  {editingRate ? (
                    <span className="inline-flex items-center gap-1">
                      $
                      <input type="number" step="0.01" min="0" value={rateDraft} onChange={(e) => setRateDraft(e.target.value)}
                        className="w-16 h-6 px-1 rounded border border-emerald-300 text-xs font-semibold text-emerald-900 bg-white"
                        data-testid={`${tag}-rate-input`} autoFocus />
                      /kg
                      <button onClick={saveRate} disabled={savingRate} className="ml-1 px-2 py-0.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-[10px] font-bold" data-testid={`save-${tag}-rate-btn`}>
                        {savingRate ? '…' : tx('Save', 'Guardar', 'OK')}
                      </button>
                      <button onClick={() => setEditingRate(false)} className="px-1.5 py-0.5 text-emerald-700 text-[10px]">×</button>
                    </span>
                  ) : (
                    <button onClick={beginEditRate} className="inline-flex items-center gap-1 underline decoration-dotted hover:text-emerald-900" data-testid={`edit-${tag}-rate-btn`} title={tx('Click to edit rate', 'Click para editar', 'Modifier')}>
                      ${(commissions.rate_usd_per_kg ?? 1).toFixed(2)}/kg <Pencil size={10} />
                    </button>
                  )}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button onClick={openPayout} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg" disabled={(commissions.total_owed || 0) <= 0} data-testid={`pay-out-${tag}-btn`}>
                  <CurrencyDollar size={16} className="mr-1.5" /> {tx('Pay Out', 'Pagar', 'Payer')}
                </Button>
              </div>
            </div>
          )}

          {customers.map((fc) => {
            const commInfo = (commissions.clients || []).find(cc => cc.id === fc.id);
            return (
              <div key={fc.id} className="kpi-card flex items-center justify-between gap-3" data-testid={`${tag}-customer-${fc.id}`}>
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <div className={`w-10 h-10 rounded-xl ${palette.avatar} flex items-center justify-center shrink-0`}>
                    <span className={`${palette.avatarText} font-bold`}>{fc.name.charAt(0).toUpperCase()}</span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-gray-900 truncate flex items-center gap-2">
                      {fc.name}
                      <span className={`px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide rounded ${palette.badgeBg}`}>{title.replace(' Clients','')}</span>
                    </p>
                    <p className="text-xs text-gray-500 truncate">{fc.email} · {fc.phone || '—'}</p>
                    <p className="text-[10px] text-gray-400 mt-0.5">
                      {tx('Tracking link', 'Enlace', 'Lien')}: <span className="font-mono">/portal/{fc.tracking_link_id}</span>
                    </p>
                    {!isViewer && commInfo && (
                      <p className="text-xs font-semibold text-emerald-700 mt-1" data-testid={`commission-${tag}-${fc.id}`}>
                        {tx(`Owed to ${title.replace(' Clients','')}`, 'Pendiente', 'Dû')}: ${commInfo.commission_owed.toFixed(2)}
                        <span className="ml-1 text-[10px] font-normal text-emerald-600/70">
                          ({commInfo.invoiced_kg} kg · {commInfo.invoiced_packages} {tx('pkgs', 'paq', 'colis')})
                        </span>
                      </p>
                    )}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-xs text-gray-500">{tx('Packages', 'Paquetes', 'Colis')}</p>
                  <p className="font-bold text-gray-900">{fc.total_packages || 0}</p>
                </div>
              </div>
            );
          })}
        </>
      )}

      {/* Payout modal */}
      {showPayoutModal && !isViewer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => !payingOut && setShowPayoutModal(false)} data-testid={`${tag}-payout-modal`}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900">{tx(`Record ${title.replace(' Clients','')} Payout`, 'Registrar Pago', 'Enregistrer Paiement')}</h3>
              <button onClick={() => setShowPayoutModal(false)} className="p-1 hover:bg-gray-100 rounded"><X size={18} /></button>
            </div>
            <p className="text-sm text-gray-500 mb-4">
              {tx('Current balance owed', 'Saldo pendiente', 'Solde dû')}: <span className="font-bold text-emerald-700">${(commissions.total_owed || 0).toFixed(2)}</span>
            </p>
            <div className="space-y-3">
              <div>
                <Label className="text-xs text-gray-600">{tx('Amount (USD)', 'Monto (USD)', 'Montant (USD)')}</Label>
                <Input type="number" step="0.01" min="0" value={payoutAmount} onChange={(e) => setPayoutAmount(e.target.value)} data-testid={`${tag}-payout-amount-input`} />
              </div>
              <div>
                <Label className="text-xs text-gray-600">{tx('Note (optional)', 'Nota (opcional)', 'Note')}</Label>
                <Input value={payoutNote} onChange={(e) => setPayoutNote(e.target.value)} placeholder={tx('e.g. Monthly settlement', 'ej. Liquidación', 'Règlement')} data-testid={`${tag}-payout-note-input`} />
              </div>
            </div>
            <div className="flex gap-2 justify-end mt-5">
              <Button variant="outline" onClick={() => setShowPayoutModal(false)} disabled={payingOut}>{tx('Cancel', 'Cancelar', 'Annuler')}</Button>
              <Button onClick={recordPayout} disabled={payingOut} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`confirm-${tag}-payout-btn`}>
                {payingOut ? '…' : tx('Record Payout', 'Registrar', 'Enregistrer')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* QR modal */}
      {showQR && signupUrl && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setShowQR(false)} data-testid={`${tag}-qr-modal`}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 text-center" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-bold text-gray-900">{title} QR</h3>
              <button onClick={() => setShowQR(false)} className="p-1 hover:bg-gray-100 rounded"><X size={18} /></button>
            </div>
            <p className="text-xs text-gray-500 mb-4">
              {tx('Scan or print on business cards & flyers.', 'Escanea o imprime.', 'Scannez ou imprimez.')}
            </p>
            <div id={`${tag}-qr-canvas`} className="flex items-center justify-center p-4 bg-white rounded-xl border border-gray-200 mb-3 mx-auto w-fit">
              <QRCodeCanvas value={signupUrl} size={240} level="H" includeMargin />
            </div>
            <code className="block text-[11px] font-mono text-gray-500 break-all mb-4">{signupUrl}</code>
            <Button onClick={downloadQR} className={`${palette.btnPrimary} w-full`} data-testid={`download-${tag}-qr-btn`}>
              <Download size={16} className="mr-1.5" /> {tx('Download PNG', 'Descargar PNG', 'Télécharger PNG')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
